/* The choice kit (ui-map-interface.md §18, R2011): the look of every
   choice control, as the owner picked it from mockup round 098. The
   game, the hub, the arcade lobby and the three labs import this sheet;
   `ui/choice/choice.ts` writes the markup.

   Every rule starts with `:is(#ui, #hub, :root)`. `:is` takes the weight
   of its heaviest argument, so a rule weighs as much as an id on every
   page, and a kit control wins over the chrome's own `#ui button` rules
   on the game page, the hub and a lab alike. A control rule names the
   element (`button.ch-pl`), so it weighs as much as the reset and comes
   after it.

   The sheet carries no breakpoint. `(pointer: coarse)` grows a control
   to the target of a finger and turns the list of a select box into a
   bottom sheet. Every colour is a token of `gen/tokens.css`, every size
   a type step, and every hover rule stands in `@media (hover: hover)`
   (R1693). The press rules stand after the hover rules, so a press shows
   on a hovered control too. A size under 11 px reads
   `max(var(--type-floor, 0px), …)`: the hub sets the floor. */

@import '../../gen/tokens.css';

/* --- the reset of a kit button --- */

:is(#ui, #hub, :root) button[class^="ch-"] {
  appearance: none;
  -webkit-appearance: none;
  position: relative;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  min-width: 0;
  min-height: 0;
  border: 0;
  border-radius: 0;
  background: none;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: inherit;
  text-transform: none;
  box-shadow: none;
  cursor: pointer;
}
:is(#ui, #hub, :root) button[class^="ch-"]::-moz-focus-inner { border: 0; padding: 0; }
:is(#ui, #hub, :root) button[class^="ch-"]:disabled { cursor: not-allowed; }
/* A link button (`linkButton`) is an anchor: it takes the box of a button. */
:is(#ui, #hub, :root) a.ch-btn { position: relative; box-sizing: border-box; cursor: pointer; }

/* The quiet count after a label, a dot of an option's own ink, and the
   radio mark. A count of 0 stands faint. */
:is(#ui, #hub, :root) .ch-n {
  font-style: normal;
  color: var(--dim);
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
}
:is(#ui, #hub, :root) :is(.on, [aria-selected="true"]) > .ch-n { color: var(--accent); }
:is(#ui, #hub, :root) .z > .ch-n { color: var(--faint); }
:is(#ui, #hub, :root) .ch-dot { display: inline-block; flex: none; width: 7px; height: 7px; border-radius: 50%; }
:is(#ui, #hub, :root) .ch-rm { position: relative; width: 18px; height: 18px; flex: none; border-radius: 50%; border: 1.5px solid var(--dim); box-sizing: border-box; }
:is(#ui, #hub, :root) .on > .ch-rm { border-color: var(--accent); }
:is(#ui, #hub, :root) .on > .ch-rm::after { content: ""; position: absolute; inset: 3px; border-radius: 50%; background: var(--accent); }

/* --- the pill group, one value: the track of card 02 ---

   One track of one width a pill, a 1 px edge round the whole group and
   2 px of room inside. The fill is the track's `::before`: its place
   reads `--i` of `--n` pills, and it slides from `--from` when the
   picked value changed since the last draw. */
:is(#ui, #hub, :root) .ch-pg {
  --ch-pill: 30px;
  position: relative;
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  flex: none;
  max-width: 100%;
  padding: 2px;
  box-sizing: border-box;
  border: 1px solid var(--edge);
  border-radius: 999px;
  vertical-align: middle;
}
:is(#ui, #hub, :root) .ch-pg.compact { --ch-pill: 26px; }
:is(#ui, #hub, :root) .ch-pg::before {
  content: "";
  position: absolute;
  z-index: 0;
  top: 2px;
  bottom: 2px;
  left: calc(2px + (100% - 4px) * var(--i, 0) / var(--n, 1));
  width: calc((100% - 4px) / var(--n, 1));
  box-sizing: border-box;
  border: 1px solid rgb(var(--accent-rgb) / 0.7);
  border-radius: 999px;
  background: rgb(var(--accent-rgb) / 0.24);
  pointer-events: none;
}
:is(#ui, #hub, :root) .ch-pg.none::before { display: none; }
:is(#ui, #hub, :root) .ch-pg.slide::before { animation: ch-slide 0.18s ease; }
@keyframes ch-slide {
  from { left: calc(2px + (100% - 4px) * var(--from, 0) / var(--n, 1)); }
}

/* --- the pill group, several values: the ticked pills of card 05 --- */
:is(#ui, #hub, :root) .ch-pm {
  --ch-pill: 30px;
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 6px;
  max-width: 100%;
  vertical-align: middle;
}
:is(#ui, #hub, :root) .ch-pm.compact { --ch-pill: 26px; }

/* One pill: 30 px painted (26 px compact) and the 44 px target of a
   finger on its `::after` (R257). In the track it has no edge of its
   own; a ticked pill has one. */
:is(#ui, #hub, :root) button.ch-pl {
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: var(--ch-pill, 30px);
  min-width: var(--ch-pill, 30px);
  padding: 0 11px;
  border: 1px solid transparent;
  border-radius: 999px;
  color: var(--ch-ink, var(--dim));
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  font-weight: var(--t-cap-weight);
  letter-spacing: 0.08em;
  line-height: 1;
  white-space: nowrap;
}
:is(#ui, #hub, :root) .compact > button.ch-pl { padding: 0 8px; }
:is(#ui, #hub, :root) .short > button.ch-pl { padding: 0 6px; letter-spacing: 0; }
:is(#ui, #hub, :root) button.ch-pl.on { color: var(--ch-ink, var(--accent)); }
:is(#ui, #hub, :root) .ch-pm > button.ch-pl { border-color: var(--edge); }
:is(#ui, #hub, :root) .ch-pm > button.ch-pl.on { border-color: rgb(var(--accent-rgb) / 0.7); background: rgb(var(--accent-rgb) / 0.24); }
:is(#ui, #hub, :root) button.ch-pl:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--accent); }
:is(#ui, #hub, :root) button.ch-pl:disabled { color: var(--faint); }
:is(#ui, #hub, :root) .ch-pm > button.ch-pl:disabled { border-style: dashed; border-color: var(--faint); background: none; }
/* A roster of names wraps (`wrap`), and a pick of the second kind (a cup
   reserve, `alt`) takes a dashed rim and a fainter fill. */
:is(#ui, #hub, :root) .ch-pm.wrap { flex-wrap: wrap; row-gap: 8px; }
:is(#ui, #hub, :root) .ch-pm > button.ch-pl.on.alt { border-style: dashed; background: rgb(var(--accent-rgb) / 0.12); }
/* The tick box of a several-values pill. */
:is(#ui, #hub, :root) .ch-tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 10px;
  height: 10px;
  box-sizing: border-box;
  border: 1.5px solid var(--faint);
  border-radius: 2px;
  font-style: normal;
}
:is(#ui, #hub, :root) .on > .ch-tick { border-color: var(--accent); background: var(--accent); }
:is(#ui, #hub, :root) .on > .ch-tick::after { content: "✓"; font-size: 8px; line-height: 1; color: rgb(var(--ink-rgb)); }

/* --- the tab strip: the box of the chat tabs (R1800) --- */
:is(#ui, #hub, :root) .ch-ts {
  --ch-tab: 36px;
  display: inline-flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  border: 1px solid rgb(var(--accent-rgb) / 0.3);
  border-radius: var(--r-button);
  overflow: hidden;
  vertical-align: middle;
}
:is(#ui, #hub, :root) .ch-ts.compact { --ch-tab: 30px; }
/* A section bar (`scroll`, R2027): the one tab strip that scrolls. Its
   tabs keep their width, and the strip scrolls sideways inside its box. */
:is(#ui, #hub, :root) .ch-ts.scroll { overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: none; }
:is(#ui, #hub, :root) .ch-ts.scroll > button.ch-tab { flex: 1 0 auto; }
:is(#ui, #hub, :root) button.ch-tab {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: var(--ch-tab, 36px);
  padding: 0 11px;
  border-right: 1px solid rgb(var(--accent-rgb) / 0.3);
  color: var(--dim);
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  font-weight: var(--t-cap-weight);
  letter-spacing: var(--t-cap-track);
  white-space: nowrap;
}
:is(#ui, #hub, :root) .ch-ts.compact > button.ch-tab { padding: 0 var(--s-2); }
:is(#ui, #hub, :root) .ch-ts.short > button.ch-tab { padding: 0 var(--s-2); letter-spacing: 0; }
:is(#ui, #hub, :root) button.ch-tab:last-child { border-right: 0; }
:is(#ui, #hub, :root) button.ch-tab.on { background: rgb(var(--accent-rgb) / 0.18); color: var(--accent); box-shadow: inset 0 -2px 0 var(--accent); }
:is(#ui, #hub, :root) button.ch-tab.z { color: var(--faint); }
:is(#ui, #hub, :root) button.ch-tab:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
:is(#ui, #hub, :root) button.ch-tab:disabled { color: var(--faint); }
:is(#ui, #hub, :root) .ch-tl { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
/* The light count of unread lines (the `lcount` of the chrome). */
:is(#ui, #hub, :root) .ch-lc {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  min-width: 16px;
  height: 16px;
  padding: 0 var(--s-2);
  box-sizing: border-box;
  border-radius: 8px;
  background: var(--hero-ink);
  color: rgb(var(--deep-rgb));
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  font-weight: 700;
  letter-spacing: 0;
}

/* --- the option list: one line a value (the language) --- */
:is(#ui, #hub, :root) .ch-ol { display: flex; flex-direction: column; gap: 6px; width: 100%; margin: 0 0 8px; }
:is(#ui, #hub, :root) button.ch-oi {
  display: flex;
  align-items: center;
  gap: 13px;
  width: 100%;
  min-height: 46px;
  padding: 0 14px;
  border: 1px solid var(--edge);
  border-radius: 8px;
  background: rgb(var(--accent-rgb) / 0.03);
  color: var(--text);
  text-align: left;
}
:is(#ui, #hub, :root) .ch-ol.compact > button.ch-oi { min-height: 36px; gap: 10px; padding: 0 10px; }
:is(#ui, #hub, :root) :is(button.ch-oi, button.ch-sbi) > b {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 400;
  color: var(--text);
  letter-spacing: 0.1em;
  font-size: var(--t-body-size);
}
:is(#ui, #hub, :root) button.ch-oi.on { border-color: rgb(var(--accent-rgb) / 0.6); background: rgb(var(--accent-rgb) / 0.1); }
:is(#ui, #hub, :root) :is(button.ch-oi, button.ch-sbi).on > b { color: var(--accent); }
:is(#ui, #hub, :root) button.ch-oi.open { border-radius: 8px 8px 0 0; border-bottom-color: rgb(var(--accent-rgb) / 0.2); }
:is(#ui, #hub, :root) button.ch-oi:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
:is(#ui, #hub, :root) button.ch-oi:disabled > b { color: var(--faint); }
:is(#ui, #hub, :root) .ch-now {
  font-style: normal;
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  font-weight: var(--t-cap-weight);
  letter-spacing: 0.12em;
  color: var(--dim);
  border: 1px solid rgb(var(--accent-rgb) / 0.22);
  border-radius: var(--r-bar);
  padding: 0 5px;
  line-height: 15px;
}
/* The confirm strip under a pick that waits (the language reloads). */
:is(#ui, #hub, :root) .ch-conf {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-top: -7px;
  padding: 11px 12px 11px 45px;
  border: 1px solid rgb(var(--accent-rgb) / 0.6);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: rgb(var(--accent-rgb) / 0.06);
  color: var(--text);
  font-size: var(--t-small-size);
}
:is(#ui, #hub, :root) .ch-conf > span:first-child { flex: 1 1 200px; }
:is(#ui, #hub, :root) .ch-confb { display: flex; gap: 8px; }

/* --- the select box: the field, and its list in a popover (card 08) or
   a bottom sheet under a finger (card 07) --- */
:is(#ui, #hub, :root) .ch-sb { position: relative; display: inline-flex; min-width: 0; max-width: 100%; vertical-align: middle; }
:is(#ui, #hub, :root) button.ch-sbf {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 190px;
  max-width: 100%;
  min-height: 40px;
  padding: 0 8px 0 10px;
  border: 1px solid rgb(var(--accent-rgb) / 0.3);
  border-radius: var(--r-button);
  background: rgb(var(--deep-rgb) / 0.5);
  color: var(--text);
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  letter-spacing: 0.12em;
  white-space: nowrap;
  text-align: left;
}
:is(#ui, #hub, :root) .ch-sb.compact > button.ch-sbf { min-height: 28px; min-width: 150px; }
:is(#ui, #hub, :root) .ch-v { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
:is(#ui, #hub, :root) .ch-ph { color: var(--faint); }
:is(#ui, #hub, :root) .ch-car { flex: none; color: var(--accent); }
:is(#ui, #hub, :root) .ch-sb:has(> .ch-sbp:popover-open) > button.ch-sbf { border-color: rgb(var(--accent-rgb) / 0.6); background: rgb(var(--accent-rgb) / 0.1); }
:is(#ui, #hub, :root) button.ch-sbf:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
/* The list as a popover under the field: the card ground, no shadow
   (DESIGN.md "No Drop Shadow Rule"). It flips over the field where no
   room stands under it (a field at the foot of the screen: the channels
   of the replay viewer). A browser with no anchor positioning shows it
   in the middle of the screen. */
:is(#ui, #hub, :root) .ch-sbp {
  position: fixed;
  inset: auto;
  top: anchor(bottom);
  left: anchor(left);
  position-try-fallbacks: flip-block;
  margin: 2px 0 0;
  min-width: 230px;
  max-width: min(300px, calc(100vw - 32px));
  max-height: min(60vh, 420px);
  overflow: auto;
  box-sizing: border-box;
  padding: 2px;
  border: 1px solid var(--edge);
  border-radius: var(--r-card);
  background: var(--g-card);
  color: var(--text);
  font: inherit;
  pointer-events: auto;
}
@supports not (anchor-name: --ch-a) {
  :is(#ui, #hub, :root) .ch-sbp { inset: 0; margin: auto; }
}
:is(#ui, #hub, :root) .ch-sbp::backdrop { background: transparent; }
:is(#ui, #hub, :root) .ch-sbp :is(.ch-grip, .ch-sbh) { display: none; }
:is(#ui, #hub, :root) .ch-sbl { display: flex; flex-direction: column; }
:is(#ui, #hub, :root) button.ch-sbi {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 28px;
  padding: 0 8px;
  border-radius: var(--r-button);
  color: var(--text);
  text-align: left;
}
:is(#ui, #hub, :root) button.ch-sbi > b { font-size: max(var(--type-floor, 0px), var(--t-cap-size)); letter-spacing: 0.12em; }
/* With a mouse the mark of a line is a tick column. */
:is(#ui, #hub, :root) .ch-sbp button.ch-sbi > .ch-rm { width: 10px; height: auto; border: 0; border-radius: 0; color: var(--accent); }
:is(#ui, #hub, :root) .ch-sbp button.ch-sbi.on > .ch-rm::after { content: "✓"; position: static; background: none; }
:is(#ui, #hub, :root) button.ch-sbi.on { background: rgb(var(--accent-rgb) / 0.14); color: var(--accent); }
:is(#ui, #hub, :root) button.ch-sbi.z > b { color: var(--faint); }
:is(#ui, #hub, :root) button.ch-sbi:disabled > b { color: var(--faint); }
:is(#ui, #hub, :root) button.ch-sbi:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
:is(#ui, #hub, :root) .ch-sbn { margin: 6px 8px 4px; color: var(--faint); font-size: var(--t-small-size); line-height: 1.4; }
:is(#ui, #hub, :root) button.ch-sbx {
  min-width: 36px;
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-button);
  color: var(--dim);
  font-size: var(--t-body-size);
}
:is(#ui, #hub, :root) button.ch-sbx:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }

/* The list as a bottom sheet (card 07): under a finger, or where the
   host asks for it (`form: 'sheet'`). A pick, a press on the scrim and
   ✕ close it. */
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp {
  inset: auto 0 0 0;
  width: 100%;
  min-width: 0;
  max-width: none;
  max-height: 80vh;
  margin: 0;
  padding: 6px 12px 12px;
  border-bottom: 0;
  border-radius: var(--r-sheet) var(--r-sheet) 0 0;
}
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp::backdrop { background: var(--g-scrim); }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp .ch-grip { display: block; width: 36px; height: 4px; margin: 2px auto 8px; border-radius: 2px; background: rgb(var(--text-rgb) / 0.25); }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp .ch-sbh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: var(--dim); font-size: max(var(--type-floor, 0px), var(--t-cap-size)); letter-spacing: 0.14em; }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp .ch-sbh > b { font-weight: var(--t-cap-weight); }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp .ch-sbl { gap: 6px; }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp button.ch-sbi {
  gap: 12px;
  min-height: var(--target-finger);
  padding: 0 12px;
  border: 1px solid var(--edge);
  border-radius: 8px;
  background: rgb(var(--accent-rgb) / 0.03);
}
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp button.ch-sbi.on { border-color: rgb(var(--accent-rgb) / 0.6); background: rgb(var(--accent-rgb) / 0.1); }

/* --- preview tiles: a picture over its word (the chart reading) --- */
:is(#ui, #hub, :root) .ch-tiles {
  display: grid;
  grid-template-columns: repeat(var(--cols, 3), minmax(0, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 500px;
}
/* A long list (a tool palette) fills the row with tiles of 66 px or more. */
:is(#ui, #hub, :root) .ch-tiles.fill { grid-template-columns: repeat(auto-fill, minmax(66px, 1fr)); gap: var(--s-2); max-width: none; }
:is(#ui, #hub, :root) button.ch-tile {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--edge);
  border-radius: 8px;
  overflow: hidden;
  background: rgb(var(--accent-rgb) / 0.03);
  color: var(--dim);
}
:is(#ui, #hub, :root) button.ch-tile > svg { display: block; width: 100%; height: auto; max-height: 66px; }
/* The word stands left after its radio mark, and its own span `ch-tn`
   ends in an ellipsis when the tile is too narrow: a centred word of a
   flex row with no wrap overflowed both edges ("YSSAL COMP", R2043). The
   radio mark and the tier word keep their width; the name gives way. */
:is(#ui, #hub, :root) button.ch-tile > b {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 7px;
  min-width: 0;
  min-height: 30px;
  padding: 0 8px;
  border-top: 1px solid var(--edge);
  font-weight: 400;
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  letter-spacing: 0.1em;
  color: var(--dim);
  white-space: nowrap;
}
:is(#ui, #hub, :root) button.ch-tile > b > .ch-tn { flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
:is(#ui, #hub, :root) .ch-tiles.compact > button.ch-tile > b { min-height: 22px; gap: 5px; padding: 0 6px; }
:is(#ui, #hub, :root) button.ch-tile > b::before {
  content: "";
  width: 8px;
  height: 8px;
  flex: none;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid var(--faint);
}
:is(#ui, #hub, :root) button.ch-tile.on { border-color: rgb(var(--accent-rgb) / 0.7); box-shadow: 0 0 0 1px rgb(var(--accent-rgb) / 0.3); }
:is(#ui, #hub, :root) button.ch-tile.on > b {
  color: var(--accent);
  background: rgb(var(--accent-rgb) / 0.16);
  box-shadow: inset 0 -2px 0 var(--accent);
  border-top-color: rgb(var(--accent-rgb) / 0.45);
}
:is(#ui, #hub, :root) button.ch-tile.on > b::before { background: var(--accent); border-color: var(--accent); }
:is(#ui, #hub, :root) button.ch-tile:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
:is(#ui, #hub, :root) button.ch-tile:disabled { opacity: 0.55; }
/* A named group (R2169): the names tell the looks apart, so a name never
   ends in an ellipsis. The row takes its whole width, a tile is wide
   enough for the longest word of a name at the coarse type floor, a name
   wraps to a second line, and the word bars of one row keep one height.
   Every other group keeps the ellipsis of R2043. */
:is(#ui, #hub, :root) .ch-tiles.named { max-width: none; grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); }
:is(#ui, #hub, :root) .ch-tiles.named > button.ch-tile > b { flex: 1 0 auto; padding: 5px 8px; line-height: 1.25; white-space: normal; }
:is(#ui, #hub, :root) .ch-tiles.named > button.ch-tile > b > .ch-tn { overflow: visible; text-overflow: clip; white-space: normal; overflow-wrap: break-word; }
/* The tier word of a look the reader does not hold, and a quiet second
   line under the word. */
:is(#ui, #hub, :root) button.ch-tile > b > .ch-mk { flex: none; font-size: max(var(--type-floor, 0px), var(--t-cap-size)); letter-spacing: 0.1em; color: var(--faint); }
:is(#ui, #hub, :root) button.ch-tile > .ch-sub { padding: 0 8px 5px; font-style: normal; text-align: start; color: var(--dim); font-size: max(var(--type-floor, 0px), var(--t-cap-size)); }

/* --- the stepper (R1776): two arrows round a value of a fixed width.
   132 px holds CUSTOM · ABCD and the longest faction name. --- */
:is(#ui, #hub, :root) .ch-step { display: inline-flex; flex: none; align-items: stretch; border: 1px solid var(--edge); border-radius: var(--r-button); overflow: hidden; vertical-align: middle; }
:is(#ui, #hub, :root) button.ch-arr {
  min-height: 36px;
  min-width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--t-title-size);
  font-weight: var(--t-title-weight);
  color: var(--dim);
  background: rgb(var(--accent-rgb) / 0.04);
}
:is(#ui, #hub, :root) .ch-step.compact > button.ch-arr { min-height: 28px; min-width: 28px; }
:is(#ui, #hub, :root) button.ch-arr:disabled { opacity: 0.4; }
:is(#ui, #hub, :root) button.ch-arr:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
:is(#ui, #hub, :root) .ch-val {
  width: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  border-left: 1px solid var(--edge);
  border-right: 1px solid var(--edge);
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  font-weight: var(--t-cap-weight);
  letter-spacing: 0.08em;
  color: var(--accent);
  background: rgb(var(--accent-rgb) / 0.12);
}
/* A count stepper (the LOADOUT sheet, R2031): the value holds a count of a
   few digits, so it takes a narrow width and figures of one width. */
:is(#ui, #hub, :root) .ch-step.count > .ch-val { width: 4.5ch; font-variant-numeric: tabular-nums; }

/* --- the tag: a state, never pressed --- */
/* A flex box drops the space between two of its parts, so the gap is the
   word space of the mono face: SEARCHING 0:12, never SEARCHING0:12
   (R2188). A tag of one text part has no gap to draw. */
:is(#ui, #hub, :root) .ch-tag {
  display: inline-flex;
  align-items: center;
  gap: 1ch;
  flex: none;
  height: 16px;
  padding: 0 5px;
  box-sizing: border-box;
  border: 1px solid rgb(var(--faint-rgb) / 0.6);
  border-radius: var(--r-bar);
  background: none;
  color: var(--dim);
  font-style: normal;
  font-weight: 400;
  font-size: max(var(--type-floor, 0px), var(--t-cap-size));
  letter-spacing: 0.14em;
  line-height: 1;
  white-space: nowrap;
  vertical-align: middle;
  cursor: default;
  user-select: none;
}
:is(#ui, #hub, :root) .ch-tag.accent { color: var(--accent); border-color: rgb(var(--accent-rgb) / 0.5); }
:is(#ui, #hub, :root) .ch-tag.gold { color: var(--gold); border-color: rgb(var(--gold-rgb) / 0.5); }
:is(#ui, #hub, :root) .ch-tag.violet { color: var(--violet); border-color: rgb(var(--violet-rgb) / 0.5); }
:is(#ui, #hub, :root) .ch-tag.good { color: var(--good); border-color: rgb(var(--good-rgb) / 0.5); }
:is(#ui, #hub, :root) .ch-tag.bad { color: var(--bad); border-color: rgb(var(--bad-rgb) / 0.5); }
:is(#ui, #hub, :root) .ch-tag.amber { color: var(--amber); border-color: rgb(var(--amber-rgb) / 0.5); }
/* A figure inside a tag (the clock of a search) keeps the ink of the tag. */
:is(#ui, #hub, :root) .ch-tag b { color: inherit; font-weight: 400; font-variant-numeric: tabular-nums; }
/* The tag is a flex row, so the space before a figure collapses: a margin keeps it. */
:is(#ui, #hub, :root) .ch-tag b:not(:first-child) { margin-left: 0.5em; }

/* --- the button: an action, or the way to another panel --- */
:is(#ui, #hub, :root) button.ch-btn,
:is(#ui, #hub, :root) a.ch-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid var(--edge);
  border-radius: var(--r-button);
  background: rgb(var(--accent-rgb) / 0.08);
  color: var(--text);
  font-size: max(var(--type-floor, 0px), var(--t-small-size));
  letter-spacing: 0.08em;
  white-space: nowrap;
  text-decoration: none;
}
:is(#ui, #hub, :root) :is(button, a).ch-btn.compact { min-height: 28px; padding: 0 10px; font-size: max(var(--type-floor, 0px), var(--t-cap-size)); }
:is(#ui, #hub, :root) :is(button, a).ch-btn.wide { width: 100%; }
:is(#ui, #hub, :root) :is(button, a).ch-btn.pri { color: var(--accent); border-color: rgb(var(--accent-rgb) / 0.55); background: rgb(var(--accent-rgb) / 0.14); font-weight: 500; }
:is(#ui, #hub, :root) :is(button, a).ch-btn.gold { color: var(--gold); border-color: rgb(var(--gold-rgb) / 0.5); background: rgb(var(--gold-rgb) / 0.08); }
:is(#ui, #hub, :root) button.ch-btn.bad { color: var(--bad); border-color: rgb(var(--bad-rgb) / 0.5); background: rgb(var(--bad-rgb) / 0.06); }
/* An armed danger button (R696). The armed face already names the act
   (R2069); the filled danger ground says that the press waits, and it
   survives a redraw, which a hover does not and a finger never had. */
:is(#ui, #hub, :root) button.ch-btn.bad.armed { background: rgb(var(--bad-rgb) / 0.18); border-color: rgb(var(--bad-rgb) / 0.7); }
/* The two faces of every armed control of the client (`armFaces`, R2059,
   R2067, R2069) stand in one grid cell. The press takes the width of the
   wider face, so it keeps its width when it arms and its neighbour does
   not move under the pointer. The face that is not in force is hidden,
   and a screen reader skips it. */
:is(#ui, #hub, :root) .ch-faces { display: inline-grid; place-items: center; }
:is(#ui, #hub, :root) .ch-faces > .face { grid-area: 1 / 1; }
:is(#ui, #hub, :root) .ch-faces:not(.on) > .face.arm,
:is(#ui, #hub, :root) .ch-faces.on > .face.rest { visibility: hidden; }
:is(#ui, #hub, :root) button.ch-btn.on { color: var(--accent); border-color: rgb(var(--accent-rgb) / 0.65); background: rgb(var(--accent-rgb) / 0.24); }
:is(#ui, #hub, :root) button.ch-btn:focus-visible, :is(#ui, #hub, :root) a.ch-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
:is(#ui, #hub, :root) button.ch-btn.gold:focus-visible { outline-color: var(--gold); }
:is(#ui, #hub, :root) button.ch-btn.bad:focus-visible { outline-color: var(--bad); }
:is(#ui, #hub, :root) button.ch-btn:disabled { color: var(--dim); background: none; border-style: dashed; border-color: var(--faint); }

/* --- a finger: the target of a finger --- */
@media (pointer: coarse) {
  /* A pill paints 30 px and takes the target on a box that grows up and
     down only, so it never takes a neighbour's edge (R257). */
  :is(#ui, #hub, :root) button.ch-pl::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: var(--target-finger);
    margin-top: calc(var(--target-finger) / -2);
  }
  :is(#ui, #hub, :root) button.ch-tab { min-height: var(--target-finger); }
  :is(#ui, #hub, :root) button.ch-btn,
  :is(#ui, #hub, :root) a.ch-btn,
  :is(#ui, #hub, :root) :is(button, a).ch-btn.compact,
  :is(#ui, #hub, :root) button.ch-sbf,
  :is(#ui, #hub, :root) button.ch-oi { min-height: var(--target-finger); }
  :is(#ui, #hub, :root) button.ch-arr { min-height: var(--target-finger); min-width: var(--target-finger); }
  :is(#ui, #hub, :root) button.ch-sbx { min-width: var(--target-finger); min-height: var(--target-finger); }
  /* The list of a select box is a bottom sheet under a finger, unless
     the host asks for the popover (`form: 'pop'`). */
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp {
    inset: auto 0 0 0;
    width: 100%;
    min-width: 0;
    max-width: none;
    max-height: 80vh;
    margin: 0;
    padding: 6px 12px 12px;
    border-bottom: 0;
    border-radius: var(--r-sheet) var(--r-sheet) 0 0;
  }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp::backdrop { background: var(--g-scrim); }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp .ch-grip { display: block; width: 36px; height: 4px; margin: 2px auto 8px; border-radius: 2px; background: rgb(var(--text-rgb) / 0.25); }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp .ch-sbh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: var(--dim); font-size: max(var(--type-floor, 0px), var(--t-cap-size)); letter-spacing: 0.14em; }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp .ch-sbl { gap: 6px; }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp button.ch-sbi {
    gap: 12px;
    min-height: var(--target-finger);
    padding: 0 12px;
    border: 1px solid var(--edge);
    border-radius: 8px;
    background: rgb(var(--accent-rgb) / 0.03);
  }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp button.ch-sbi > .ch-rm { width: 18px; height: 18px; border: 1.5px solid var(--dim); border-radius: 50%; }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp button.ch-sbi.on > .ch-rm { border-color: var(--accent); }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp button.ch-sbi.on > .ch-rm::after { content: ""; position: absolute; inset: 3px; border-radius: 50%; background: var(--accent); }
  :is(#ui, #hub, :root) .ch-sb:not(.pop) > .ch-sbp button.ch-sbi.on { border-color: rgb(var(--accent-rgb) / 0.6); background: rgb(var(--accent-rgb) / 0.1); }
}
/* The sheet a host asks for draws the radio mark of the option list. */
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp button.ch-sbi > .ch-rm { width: 18px; height: 18px; border: 1.5px solid var(--dim); border-radius: 50%; }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp button.ch-sbi.on > .ch-rm { border-color: var(--accent); }
:is(#ui, #hub, :root) .ch-sb.sheet > .ch-sbp button.ch-sbi.on > .ch-rm::after { content: ""; position: absolute; inset: 3px; border-radius: 50%; background: var(--accent); }

/* A locked option that still takes its press (`tell`, R701): the faint
   ink and the strike of a missing gate (R45). */
:is(#ui, #hub, :root) button.ch-pl[aria-disabled="true"]:not(:disabled) { color: var(--faint); text-decoration: line-through; cursor: not-allowed; }

@media (prefers-reduced-motion: reduce) {
  :is(#ui, #hub, :root) .ch-pg.slide::before { animation: none; }
}

/* --- hover: a pointer that hovers alone (R1693). A control that rests
   in its hover colours (a picked pill, an open tab, the primary button)
   takes a brighter fill. --- */
@media (hover: hover) {
  :is(#ui, #hub, :root) button.ch-pl:hover:not(:disabled) { color: var(--text); background: rgb(var(--accent-rgb) / 0.07); }
  :is(#ui, #hub, :root) .ch-pm > button.ch-pl:hover:not(:disabled) { border-color: rgb(var(--accent-rgb) / 0.5); }
  :is(#ui, #hub, :root) button.ch-pl.on:hover:not(:disabled) { color: var(--hover-ink); background: rgb(var(--accent-rgb) / 0.3); }
  :is(#ui, #hub, :root) button.ch-tab:hover:not(:disabled) { color: var(--text); background: rgb(var(--accent-rgb) / 0.1); }
  :is(#ui, #hub, :root) button.ch-tab.on:hover:not(:disabled) { color: var(--hover-ink); background: rgb(var(--accent-rgb) / 0.28); }
  :is(#ui, #hub, :root) button.ch-oi:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.08); }
  :is(#ui, #hub, :root) button.ch-oi.on:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.18); }
  :is(#ui, #hub, :root) button.ch-sbf:hover:not(:disabled) { border-color: rgb(var(--accent-rgb) / 0.55); background: rgb(var(--accent-rgb) / 0.06); }
  :is(#ui, #hub, :root) button.ch-sbi:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.08); }
  :is(#ui, #hub, :root) button.ch-sbi.on:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.22); }
  :is(#ui, #hub, :root) button.ch-sbx:hover { background: rgb(var(--accent-rgb) / 0.18); color: var(--hover-ink); }
  :is(#ui, #hub, :root) button.ch-tile:hover:not(:disabled) { border-color: rgb(var(--accent-rgb) / 0.45); }
  :is(#ui, #hub, :root) button.ch-tile:hover:not(:disabled) > b { color: var(--text); }
  :is(#ui, #hub, :root) button.ch-tile.on:hover:not(:disabled) { border-color: rgb(var(--accent-rgb) / 0.85); }
  :is(#ui, #hub, :root) button.ch-tile.on:hover:not(:disabled) > b { color: var(--hover-ink); background: rgb(var(--accent-rgb) / 0.26); }
  :is(#ui, #hub, :root) button.ch-arr:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.12); color: var(--text); }
  :is(#ui, #hub, :root) button.ch-btn:hover:not(:disabled), :is(#ui, #hub, :root) a.ch-btn:hover { background: rgb(var(--accent-rgb) / 0.2); color: var(--hover-ink); }
  :is(#ui, #hub, :root) button.ch-btn.pri:hover:not(:disabled), :is(#ui, #hub, :root) a.ch-btn.pri:hover { background: rgb(var(--accent-rgb) / 0.26); color: var(--hover-ink); }
  :is(#ui, #hub, :root) button.ch-btn.gold:hover:not(:disabled), :is(#ui, #hub, :root) a.ch-btn.gold:hover { background: rgb(var(--gold-rgb) / 0.2); color: var(--gold); }
  :is(#ui, #hub, :root) button.ch-btn.bad:hover:not(:disabled) { background: rgb(var(--bad-rgb) / 0.18); color: var(--bad); }
  :is(#ui, #hub, :root) button.ch-btn.bad.armed:hover:not(:disabled) { background: rgb(var(--bad-rgb) / 0.28); }
  :is(#ui, #hub, :root) button.ch-btn.on:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.32); color: var(--hover-ink); }
  /* A telling locked pill (`tell`) takes no hover. The rule stands last,
     so it wins over the hover of a picked pill. */
  :is(#ui, #hub, :root) :is(.ch-pg, .ch-pm) > button.ch-pl[aria-disabled="true"]:hover { color: var(--faint); background: none; }
}

/* --- the press: one step past the hover, on every device --- */
:is(#ui, #hub, :root) button.ch-pl:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.16); }
:is(#ui, #hub, :root) button.ch-pl.on:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.36); }
:is(#ui, #hub, :root) button.ch-tab:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.2); }
:is(#ui, #hub, :root) button.ch-tab.on:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.34); }
:is(#ui, #hub, :root) :is(button.ch-oi, button.ch-sbi):active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.16); }
:is(#ui, #hub, :root) :is(button.ch-oi, button.ch-sbi).on:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.26); }
:is(#ui, #hub, :root) button.ch-sbf:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.14); }
:is(#ui, #hub, :root) button.ch-sbx:active { background: rgb(var(--accent-rgb) / 0.26); }
:is(#ui, #hub, :root) button.ch-tile:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.12); }
:is(#ui, #hub, :root) button.ch-arr:active:not(:disabled) { background: rgb(var(--accent-rgb) / 0.22); }
:is(#ui, #hub, :root) button.ch-btn:active:not(:disabled), :is(#ui, #hub, :root) a.ch-btn:active {
  background: rgb(var(--accent-rgb) / 0.3);
  box-shadow: inset 0 2px 0 rgb(var(--deep-rgb) / 0.55);
  transform: translateY(1px);
}
:is(#ui, #hub, :root) button.ch-btn.pri:active:not(:disabled), :is(#ui, #hub, :root) a.ch-btn.pri:active { background: rgb(var(--accent-rgb) / 0.36); }
:is(#ui, #hub, :root) button.ch-btn.gold:active:not(:disabled), :is(#ui, #hub, :root) a.ch-btn.gold:active { background: rgb(var(--gold-rgb) / 0.3); }
:is(#ui, #hub, :root) button.ch-btn.bad:active:not(:disabled) { background: rgb(var(--bad-rgb) / 0.3); }

/* The key cap of a pill: the hotkey that would press it (the round key). */
:is(#ui, #hub, :root) button.ch-pl > .ck-key {
  margin-left: 4px;
  padding: 0 4px;
  border: 1px solid currentColor;
  border-radius: 3px;
  font: inherit;
  font-size: 0.8em;
  opacity: 0.7;
}
@media (pointer: coarse) {
  :is(#ui, #hub, :root) button.ch-pl > .ck-key { display: none; }
}
